'use client';

import { action, autorun, makeObservable, observable } from 'mobx';
import samplesSelectorModel from '../SamplesSelector/SamplesSelectorModel';
import samplesModel, { popupCoords, SAMPLES_MODE } from '../SamplesModel';
import { apiSwagger } from '../../../shared/api';
import { createI18nObj, rightLanguage, setValueToi18nObj } from '@/sharedTypes/helpers';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { SamplesEditorModel } from '../SamplesEditor/SamplesEditorModel';
import samplesDiagnosticPopupModel from '../SamplesDiagnosticPopup/SamplesDiagnosticPopupModel';
import { ManagerEntity } from '@/shared/globalFunctions';
import { safeWindow } from '@/sharedTypes/browserUtils';
import toast from '@/components/ui/custom-toast';
import { t } from '@/shared/i18n';
import { v4 } from 'uuid';
import type { Author, Category } from '@/sharedTypes/Api';
import { I18nObject } from '@/sharedTypes/helpers';
import etaLoader from '@/lib/EtaLoader';

interface metadataInterface {
  title: Record<string, string>;
  author: string; // ObjectId string, not Author object
  midiLoaded: boolean;
  midiWrongNotesCount: number;
}

// Default placeholder values that should not be used for sample uploads
export const DEFAULT_CATEGORY_EN = 'sample category';
export const DEFAULT_CATEGORY_RU = 'пример категории';

export class SamplesUploaderModel {
  uploadCategory: Category = {
    _id: v4(),
    en: DEFAULT_CATEGORY_EN,
    ru: DEFAULT_CATEGORY_RU,
  };
  createdCategories: Category[] = [];
  get allCategories(): Category[] {
    return samplesSelectorModel.categories.concat(this.createdCategories);
  }

  loadedTitle: I18nObject = {
    en: 'Sample title',
    ru: 'Пример заголовка'
  };

  loadedAuthor: Author = {
    _id: v4(),
    en: 'sample author',
    ru: 'пример автора',
  };
  loadedMusicxml: File | null = null;
  loadedMidi: File | null = null;

  /** Whether the loaded MusicXML file is valid (timemap can be computed) */
  musicxmlValid: boolean = false;
  /** Whether MusicXML validation is in progress */
  musicxmlValidating: boolean = false;

  reset() {
    this.loadedTitle = {
      en: "",
      ru: ""
    };
    this.loadedAuthor = {
      _id: v4(),
      ...createI18nObj(),
    };
    this.loadedMusicxml = null;
    this.loadedMidi = null;
    this.createdCategories = [];
    this.musicxmlValid = false;
    this.musicxmlValidating = false;
  }

  constructor() {
    makeObservable(this, {
      uploadCategory: observable,
      loadedTitle: observable,
      loadedAuthor: observable,
      loadedMusicxml: observable,
      loadedMidi: observable,
      musicxmlValid: observable,
      musicxmlValidating: observable,
      loadNewMusicxmlFile: action,
      uploadLoadedMusicxml: action,
    });
    autorun(() => {
      if (
        samplesSelectorModel &&
        samplesSelectorModel.samples &&
        samplesSelectorModel.samples.length
      ) {
        const samplesSelectorCategories = samplesSelectorModel.categories;
        if (samplesSelectorCategories.length > 0) {
          this.uploadCategory = samplesSelectorCategories[0];
        }
      }
    });
  }

  async loadNewMusicxmlFile(file?: File) {
    if (!file) {
      return;
    }

    this.loadedMusicxml = file;
    this.musicxmlValid = false;
    this.musicxmlValidating = true;

    // Start capturing console errors/warnings
    samplesDiagnosticPopupModel.resetWarnings();
    samplesDiagnosticPopupModel.startMessageListening();

    try {
      if (!ManagerEntity?.core) {
        throw new Error('Core functionality not available');
      }

      const fileBuffer = await this.readFileAsArrayBuffer(file);
      const musicXml = await this.getMusicXml(fileBuffer);

      if (!musicXml) {
        throw new Error('Failed to read MusicXML content');
      }

      // MusicXML validation is now done server-side by verovio-cpp-server
      // Client-side just checks if file is readable
      samplesDiagnosticPopupModel.stopMessageListening();
      this.musicxmlValid = true;
      this.musicxmlValidating = false;
    } catch (error) {
      samplesDiagnosticPopupModel.stopMessageListening();
      console.error('Error loading MusicXML:', error);
      this.musicxmlValid = false;
      this.musicxmlValidating = false;
      this.loadedMusicxml = null;
      toast({
        title: t('error'),
        description: t('toast.musicxml_load_error_desc'),
        type: 'error',
      });
      throw error; // Re-throw so the UI can handle it
    }
  }

  loadNewMidiFile(file?: File) {
    if (file) {
      this.loadedMidi = file;
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  readFileAsArrayBuffer(file: any): Promise<ArrayBuffer> {
    return new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result as ArrayBuffer);
      };
      fr.readAsArrayBuffer(file);
    });
  }

  getMusicXml(fileBuffer: ArrayBuffer): Promise<string> {
    return Promise.resolve(new TextDecoder().decode(fileBuffer));
  }

  /**
   * Check if a category contains default placeholder values that should be blocked
   * Returns array of invalid language codes (e.g., ['EN', 'RU']) or empty array if valid
   */
  getInvalidCategoryLanguages(category: Category): string[] {
    const invalidLanguages: string[] = [];
    if (category.en && category.en.toLowerCase() === DEFAULT_CATEGORY_EN) {
      invalidLanguages.push('EN');
    }
    if (category.ru && category.ru.toLowerCase() === DEFAULT_CATEGORY_RU) {
      invalidLanguages.push('RU');
    }
    return invalidLanguages;
  }

  async uploadLoadedMusicxml() {
    try {
      if (!ManagerEntity?.core) {
        console.error('Ошибка: Основные функции недоступны');
        return Promise.reject('Core functionality not available');
      }

      if (!this.loadedMusicxml || !this.loadedAuthor || !this.loadedTitle) {
        toast({
          title: t('error'),
          description: t('samples.uploader.missing_required_fields'),
          type: 'error',
        });
        return Promise.reject('Missing required fields');
      }

      // Validate category is not a placeholder/default value
      const invalidLanguages = this.getInvalidCategoryLanguages(this.uploadCategory);
      if (invalidLanguages.length > 0) {
        toast({
          title: t('error'),
          description: t('samples.uploader.invalid_category', { languages: invalidLanguages.join(' and ') }),
          type: 'error',
        });
        return Promise.reject('Category contains placeholder values');
      }

      // Ensure MusicXML is valid before uploading
      if (!this.musicxmlValid) {
        toast({
          title: t('error'),
          description: t('toast.timemap_computation_error_desc'),
          type: 'error',
        });
        return Promise.reject('MusicXML is invalid');
      }

      // safeWindow.spinner.show('Uploading musicxml...', popupCoords);
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setContext('uploadXML');
        etaLoader.setStage('uploadXML_start', 0);
      }

      const fileBuffer = await this.readFileAsArrayBuffer(this.loadedMusicxml);
      const musicXml = await this.getMusicXml(fileBuffer);
      if (!musicXml || !ManagerEntity?.core) {
        throw new Error('Invalid musicXml or core not available');
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 0.2);
      }      
    const createdAuthorRes = await apiSwagger.authors.authorsCreate({
      en: this.loadedAuthor.en,
      ru: this.loadedAuthor.ru,
    });
    const createdAuthor = await createdAuthorRes.json();
    const author: Author = createdAuthor.data;
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('uploadXML_start', 0.5);
    }
  const createdCategoryRes = await apiSwagger.categories.categoriesCreate({
    en: this.uploadCategory.en,
    ru: this.uploadCategory.ru,
  });
  const createdCategory = await createdCategoryRes.json();
  const category: Category = createdCategory.data;
  if(typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('uploadXML_start', 0.7);
  }
// - Then modify the sample creation request to use these IDs
    const title = { ...this.loadedTitle };
    
      const metadata: metadataInterface = {
        title,
        author: author._id, // ObjectId
        midiLoaded: false,
        midiWrongNotesCount: 0,
      };
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 0.8);
      }
      // Cache/timemap is now generated server-side by verovio-cpp-server
      // No client-side computation needed
      const sample = await this.createSample(
        category._id,
        this.loadedMusicxml!.name,
        musicXml,
        metadata,
        popupCoords
      );

      if (!sample) {
        throw new Error('Failed to create sample');
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 1);
        etaLoader.setStage('uploadXML_done', 0);
      }
      if (this.loadedMidi) {
        if(typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('uploadXML_done', 0.5);
        }
        // Upload MIDI file to the sample - backend handles timemap/cache
        const res = await SamplesEditorModel.updateMidi(
          this.loadedMidi!,
          sample
        );

        if (!res) {
          triggerEvent('midiFailed');
          throw new Error('Failed to update midi');
        }
        triggerEvent('midiSuccess');
        triggerEvent('Cache with midi update');
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_done', 0.8);
      }
      triggerEvent('Sample uploaded');

      // Success: refresh samples, reset form, and go back to select mode
      await samplesSelectorModel.refreshSamples();
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_done', 1);
      }
      samplesModel.currentMode = SAMPLES_MODE.SELECT;
      this.reset();
    } catch (error: any) {
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_done', 1);
      }
      console.error('Error uploading musicxml:', error);
      // If MIDI was loaded, fire midiFailed so tests don't hang
      if (this.loadedMidi) {
        triggerEvent('midiFailed');
      }
      // Fire upload-failed so tests waiting for upload result don't hang
      triggerEvent('upload-failed', { error: error.message });
      toast({
        title: t('error'),
        description: error.message || t('samples.uploader.upload_failed'),
        type: 'error',
      });
      // On error: don't reset form so user can retry
    }
  }

  createSample(
    category: string,
    musicxmlFilename: string,
    musicxml: string,
    metadata: any['metadata'],
    popupCoords?: any | undefined
  ): Promise<any | undefined> {
    // Cache/timemap is generated server-side, no longer sent from client
    const requestBody = {
      musicxml,
      musicxmlFilename,
      metadata,
      category,
    };
    return apiSwagger.samples
      .samplesCreate(requestBody as any)
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error(res.error.error);
      })
      .then((resParsed) => resParsed.data)
      .catch((error) => {
        console.error('Ошибка: ' + error.message);
        return undefined;
      });
  }

  static replaceCache(
    sample: Record<string, any>,
    cache: object
  ): Promise<boolean> {
    const cacheStr = JSON.stringify(cache);
    const requestBody = {
      sampleId: sample._id,
      cache: cacheStr,
      cacheLength: cacheStr.length.toString(),
    };

    return apiSwagger.samples
      .cacheUpdate(requestBody)
      .then((res) => {
        if (res.ok) {
          return res.json().then(() => {
            return true;
          });
        }
        console.error('[SamplesUploader] Error updating cache');
        return false;
      })
      .catch(() => {
        console.error('[SamplesUploader] Error updating cache');
        return false;
      });
  }
}

const samplesUploaderModel = new SamplesUploaderModel();
export default samplesUploaderModel;
